import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp, 
  Ticket, 
  DollarSign,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Calendar,
  Users
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const COLORS = ['#7c3aed', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6'];

// Pick a sensible Intl locale per currency so symbols render correctly.
const CURRENCY_LOCALE = {
  NGN: 'en-NG', GHS: 'en-GH', KES: 'en-KE', ZAR: 'en-ZA',
  USD: 'en-US', CAD: 'en-CA', GBP: 'en-GB', EUR: 'en-IE',
};

function formatCurrency(amount, currency = 'NGN') {
  const code = (currency || 'NGN').toUpperCase();
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALE[code] || 'en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  } catch {
    // Fallback if the code is unknown to Intl
    return `${code} ${Math.round(amount || 0).toLocaleString()}`;
  }
}

/**
 * Render a per-currency revenue map as a single string, e.g. {NGN: 18000, USD: 48} → "NGN 18,000 / USD 48".
 * Falls back to a single-currency render when only one currency has revenue (or zero with the supplied default).
 */
function formatRevenueByCurrency(byCurrency, fallbackCurrency = 'NGN') {
  const entries = Object.entries(byCurrency || {}).filter(([, v]) => Number(v) > 0);
  if (entries.length === 0) return formatCurrency(0, fallbackCurrency);
  if (entries.length === 1) {
    const [cur, total] = entries[0];
    return formatCurrency(total, cur);
  }
  return entries.map(([cur, total]) => formatCurrency(total, cur)).join(' / ');
}

/**
 * Render the revenue for an event. Prefers revenue_by_currency (when present and non-empty)
 * so multi-currency events show "NGN 25,000 / USD 30" instead of a single misleading naive sum.
 */
function formatEventRevenue(event) {
  return formatRevenueByCurrency(event?.revenue_by_currency, event?.currency || 'NGN');
}

function StatCard({ title, value, subtitle, icon: Icon, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-500/10 text-green-500',
    blue: 'bg-blue-500/10 text-blue-500',
    amber: 'bg-amber-500/10 text-amber-500',
    purple: 'bg-purple-500/10 text-purple-500',
  };

  return (
    <Card className="bg-card/50 border-border/50">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1 break-words leading-tight" data-testid={`statcard-value-${title.replace(/\s+/g, '-').toLowerCase()}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EventPerformanceCard({ event, expanded, onToggle }) {
  const eventCurrency = event.currency || 'NGN';
  const breakdownCurrencies = Object.keys(event.revenue_by_currency || {});
  const isMultiCurrency = breakdownCurrencies.length > 1;
  return (
    <Card className="bg-card/50 border-border/50" data-testid={`event-perf-${event.event_id}`}>
      <CardContent className="p-4">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={onToggle}
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold">{event.title}</h3>
              <Badge variant={event.is_published ? 'default' : 'secondary'}>
                {event.is_published ? 'Published' : 'Draft'}
              </Badge>
              <Badge variant="outline" className="text-xs" data-testid={`event-currency-${event.event_id}`}>
                {eventCurrency}
              </Badge>
              {isMultiCurrency && (
                <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/40">
                  multi-currency
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              By {event.organizer_name} • {event.date ? format(new Date(event.date), 'MMM d, yyyy') : 'No date'}
            </p>
            <div className="flex items-center gap-4 mt-2 flex-wrap">
              <span className="text-sm">
                <span className="font-medium text-green-500">{event.total_sold}</span>
                <span className="text-muted-foreground">/{event.total_quantity} sold</span>
              </span>
              <span className="text-sm font-medium text-primary" data-testid={`event-revenue-${event.event_id}`}>
                {formatEventRevenue(event)}
              </span>
              <span className="text-sm text-muted-foreground">
                {event.sell_through_rate}% sell-through
              </span>
            </div>
          </div>
          <Button variant="ghost" size="icon">
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </Button>
        </div>
        
        {expanded && event.ticket_breakdown.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <h4 className="text-sm font-medium mb-3">Ticket Type Breakdown</h4>
            <div className="space-y-3">
              {event.ticket_breakdown.map((ticket, idx) => (
                <div
                  key={ticket.name || idx}
                  className={`flex items-center justify-between p-3 rounded-lg ${ticket.is_deleted ? 'bg-muted/30 opacity-70' : 'bg-muted/50'}`}
                  data-testid={ticket.is_deleted ? `ticket-row-deleted-${idx}` : `ticket-row-${idx}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{ticket.name}</p>
                      {ticket.is_deleted && (
                        <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/40 text-xs" data-testid="ticket-deleted-badge">
                          Deleted
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(ticket.price, eventCurrency)} per ticket
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-green-500">
                      {ticket.quantity === null || ticket.quantity === undefined
                        ? `${ticket.sold} sold (Unlimited)`
                        : `${ticket.sold}/${ticket.quantity} sold`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(ticket.revenue, eventCurrency)} • {ticket.sell_through_rate}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {isMultiCurrency && (
              <p className="text-xs text-muted-foreground mt-3">
                Note: per-ticket revenue is shown in the event's declared currency ({eventCurrency}). The header
                row above reflects the actual mix of buyer-paid amounts in each currency.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminAnalytics() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [eventsPerformance, setEventsPerformance] = useState([]);
  const [ticketTypeSummary, setTicketTypeSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEvents, setExpandedEvents] = useState({});

  // Only super_admin can access
  const isSuperAdmin = user?.admin_role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchData();
    }
  }, [isSuperAdmin]);

  // Role check - only super admin
  if (!authLoading && (!isAdmin || !isSuperAdmin)) {
    return <Navigate to="/admin" replace />;
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const [performanceRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/analytics/events-performance`),
        axios.get(`${API_URL}/api/admin/analytics/ticket-types-summary`)
      ]);
      setEventsPerformance(performanceRes.data);
      setTicketTypeSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const toggleEventExpanded = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  if (loading || authLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="admin-analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-1">Platform Analytics</h1>
            <p className="text-muted-foreground">
              All events performance and ticket sales breakdown
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Revenue"
            value={formatRevenueByCurrency(ticketTypeSummary?.total_revenue_by_currency)}
            subtitle="All events"
            icon={DollarSign}
            color="green"
          />
          <StatCard
            title="Tickets Sold"
            value={ticketTypeSummary?.total_sold || 0}
            subtitle="Across all events"
            icon={Ticket}
            color="blue"
          />
          <StatCard
            title="Total Events"
            value={eventsPerformance.length}
            subtitle="All time"
            icon={Calendar}
            color="purple"
          />
          <StatCard
            title="Ticket Types"
            value={ticketTypeSummary?.ticket_type_count || 0}
            subtitle="Unique types"
            icon={BarChart3}
            color="amber"
          />
        </div>

        {/* Ticket Type Summary Chart */}
        {ticketTypeSummary?.breakdown?.length > 0 && (
          <Card className="bg-card/50 border-border/50 mb-8">
            <CardHeader>
              <CardTitle>Revenue by Ticket Type</CardTitle>
              <CardDescription>
                Performance breakdown across all ticket types
                {(() => {
                  const cur = Object.keys(ticketTypeSummary.total_revenue_by_currency || {});
                  if (cur.length <= 1) return null;
                  return (
                    <span className="block mt-1 text-amber-500 text-xs">
                      Platform has revenue in {cur.length} currencies ({cur.join(', ')}). The chart below shows the
                      naive sum — see the Revenue column in the table for the per-currency breakdown.
                    </span>
                  );
                })()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bar Chart */}
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ticketTypeSummary.breakdown.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        type="number" 
                        stroke="#666"
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        stroke="#666" 
                        width={120}
                        tickFormatter={(value) => value.length > 15 ? value.slice(0, 15) + '...' : value}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                        formatter={(_value, _name, props) => [
                          formatRevenueByCurrency(props?.payload?.revenue_by_currency),
                          'Revenue'
                        ]}
                      />
                      <Bar dataKey="total_revenue" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Pie Chart */}
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={ticketTypeSummary.breakdown.slice(0, 8)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="total_sold"
                        nameKey="name"
                        label={({ name, percent }) => `${name.slice(0, 10)}${name.length > 10 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {ticketTypeSummary.breakdown.slice(0, 8).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                        formatter={(value, name) => [value, `${name} Sold`]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Summary Table */}
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-medium">Ticket Type</th>
                      <th className="text-right py-3 px-2 font-medium">Events</th>
                      <th className="text-right py-3 px-2 font-medium">Quantity</th>
                      <th className="text-right py-3 px-2 font-medium">Sold</th>
                      <th className="text-right py-3 px-2 font-medium">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketTypeSummary.breakdown.map((type, idx) => (
                      <tr key={type.name || idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            {type.name}
                          </div>
                        </td>
                        <td className="text-right py-3 px-2 text-muted-foreground">{type.event_count}</td>
                        <td className="text-right py-3 px-2 text-muted-foreground">{type.total_quantity}</td>
                        <td className="text-right py-3 px-2 font-medium text-green-500">{type.total_sold}</td>
                        <td className="text-right py-3 px-2 font-medium" data-testid={`type-revenue-${type.name}`}>
                          {formatRevenueByCurrency(type.revenue_by_currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Events Performance */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">All Events Performance</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Click on an event to see detailed ticket type breakdown
          </p>
          
          {eventsPerformance.length > 0 ? (
            <div className="space-y-3">
              {eventsPerformance.map((event) => (
                <EventPerformanceCard
                  key={event.event_id}
                  event={event}
                  expanded={expandedEvents[event.event_id]}
                  onToggle={() => toggleEventExpanded(event.event_id)}
                />
              ))}
            </div>
          ) : (
            <Card className="bg-card/50 border-border/50">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <BarChart3 className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No events yet</h3>
                <p className="text-muted-foreground text-center">
                  Event performance data will appear here once events are created
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
